/**
 * Minimal auth context — tracks whether a JWT is present (localStorage
 * "jwt", same key memoneo uses) and the account's Chouette-side profile
 * (`GET /chouette/me`: credit balances + whether a kid PIN is set).
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from "react";
import { getMe, currentUserId } from "./dataSource";
import { setUser as setSentryUser } from "./sentry";
import type { Me } from "./types";

interface AuthApi {
  isAuthenticated: boolean;
  me: Me | null;
  loadingMe: boolean;
  refreshMe: () => Promise<void>;
  logout: () => void;
  setAuthenticated: () => void;
}

const AuthCtx = createContext<AuthApi | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => !!localStorage.getItem("jwt"));
  const [me, setMe] = useState<Me | null>(null);
  const [loadingMe, setLoadingMe] = useState(false);

  const refreshMe = useCallback(async () => {
    if (!localStorage.getItem("jwt")) {
      setMe(null);
      return;
    }
    setLoadingMe(true);
    try {
      const value = await getMe();
      setMe(value);
    } catch {
      setMe(null);
    } finally {
      setLoadingMe(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) void refreshMe();
  }, [isAuthenticated, refreshMe]);

  // Sentry user context (plan Lot A.3) — the account id only, no email/name
  // (Sentry.init's `sendDefaultPii: false` already covers the SDK's own
  // defaults; this is the one place the app itself hands it an identifier).
  // Reacts to `isAuthenticated` rather than being called only from
  // `setAuthenticated`/`logout` below, so a page load that starts already
  // signed in (a returning session) tags events too, not just a fresh login.
  useEffect(() => {
    setSentryUser(isAuthenticated ? { id: currentUserId() ?? undefined } : null);
  }, [isAuthenticated]);

  const logout = useCallback(() => {
    localStorage.removeItem("jwt");
    setIsAuthenticated(false);
    setMe(null);
  }, []);

  const setAuthenticated = useCallback(() => setIsAuthenticated(true), []);

  const api = useMemo<AuthApi>(
    () => ({ isAuthenticated, me, loadingMe, refreshMe, logout, setAuthenticated }),
    [isAuthenticated, me, loadingMe, refreshMe, logout, setAuthenticated],
  );

  return <AuthCtx.Provider value={api}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthApi {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
